import React, { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import FiberManualRecordSharpIcon from '@material-ui/icons/FiberManualRecordSharp';
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { push } from 'connected-react-router';

import { AlertMessages, FormActionButtons, FormSelect, FormTextField, Loading, Switch } from '../../components';
import {
  executeExpiryJob,
  executeExpiryReportingJob,
  fetchAPIKeySettings,
  updateAPIKeySettings,
} from '../../actions/apiKeySettings';
import {
  getIsError,
  getErrors,
  getErrorFields,
  getExpiryJobStatus,
  getIsExecutingExpiryJob,
  getReportingJobStatus,
  getIsExecutingReportingJob,
  getIsLoading,
  getAPIKeySettings,
  getUpdateKeySettingsStatus,
} from '../../reducers/apiKeySettings';
import { getUserDetails } from '../../reducers/portalConfig';
import styles from "./styles";
import { getI18n, getI18nFormattedMessage } from "../../utils/intl";
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  FAIL,
  SUCCESS,
} from "../../constants";

const DAILY = 'DAILY';
const WEEKLY = 'WEEKLY';
const BIWEEKLY = 'BIWEEKLY';
const MONTHLY = 'MONTHLY';

const KEY_EXPIRY_REPORT_CADENCE = [{
    uuid: DAILY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.daily'),
  }, {
    uuid: WEEKLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.weekly'),
  }, {
    uuid: BIWEEKLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.biweekly'),
  }, {
    uuid: MONTHLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.monthly'),
  }];

const MON_DAY = 'MONDAY';
const TUES_DAY = 'TUESDAY';
const WEDNES_DAY = 'WEDNESDAY';
const THURS_DAY = 'THURSDAY';
const FRI_DAY = 'FRIDAY';
const SATUR_DAY = 'SATURDAY';
const SUN_DAY = 'SUNDAY';

const WEEK_REPORT_DAY_OPTIONS = [{
    uuid: MON_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.monday'),
  },
  {
    uuid: TUES_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.tuesday'),
  },
  {
    uuid: WEDNES_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.wednesday'),
  },
  {
    uuid: THURS_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.thursday'),
  },
  {
    uuid: FRI_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.friday'),
  },
  {
    uuid: SATUR_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.saturday'),
  },
  {
    uuid: SUN_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.sunday'),
  }];

const FIRST_DAY = 'FIRST';
const FIFTEENTH_DAY = 'FIFTEENTH';
const LAST_DAY = 'LAST';

const MONTH_REPORT_DAY_OPTIONS = [{
    uuid: FIRST_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.firstday'),
  },
  {
    uuid: FIFTEENTH_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.fifteenthday'),
  },
  {
    uuid: LAST_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.lastday'),
  }];

const HASH_ALGORITHM_OPTIONS = [
  {
    uuid: 'SHA-512',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha512'),
  }, {
    uuid: 'SHA-384',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha384'),
  }, {
    uuid: 'SHA-256',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha256'),
  }, {
    uuid: 'SHA-1',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha1'),
  }, {
    uuid: 'MD5',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.md5'),
  },
];

const areValuesDifferent = (valObj1, valObj2) => {
  const {
    allowPlainSecrets: allowPlainSecrets1,
    allowHashedSecrets: allowHashedSecrets1,
    algorithm: algorithm1,
    notificationsEnabled: notificationsEnabled1,
    expireKeys: expireKeys1,
    daysUntilExpiry: daysUntilExpiry1,
    reportCadence: reportCadence1,
    reportDay: reportDay1,
    sendReportToAdmin: sendReportToAdmin1,
    startReporting: startReporting1,
    urgentReporting : urgentReporting1,
  } = valObj1;
  const {
    allowPlainSecrets: allowPlainSecrets2,
    allowHashedSecrets: allowHashedSecrets2,
    algorithm: algorithm2,
    notificationsEnabled: notificationsEnabled2,
    expireKeys: expireKeys2,
    daysUntilExpiry: daysUntilExpiry2,
    reportCadence: reportCadence2,
    reportDay: reportDay2,
    sendReportToAdmin: sendReportToAdmin2,
    startReporting: startReporting2,
    urgentReporting : urgentReporting2,
  } = valObj2;

  if (
    (allowPlainSecrets1 !== allowPlainSecrets2) ||
    (allowHashedSecrets1 !== allowHashedSecrets2) ||
    ((allowHashedSecrets1 === allowHashedSecrets2) &&
    allowHashedSecrets2 && (algorithm1 !== algorithm2))
  ) {
    return true;
  }

  if (expireKeys1 !== expireKeys2) {
    return true;
  }

  if (
    // eslint-disable-next-line eqeqeq
    daysUntilExpiry1 != daysUntilExpiry2
  ) {
    return true;
  }

  if (notificationsEnabled1 !== notificationsEnabled2) {
    return true;
  }

  if (
    (reportCadence1 && reportCadence2 && (reportCadence1 !== reportCadence2)) ||
    (reportDay1 && reportDay2 && (reportDay1 !== reportDay2)) ||
    (sendReportToAdmin1 !== sendReportToAdmin2) ||
    (startReporting1 !== startReporting2) ||
    (urgentReporting1 !== urgentReporting2)
  ) {
    return true;
  }

  return false;
};

const valuesToPayload = (valuesObj = { }) => {
  const {
    allowPlainSecrets,
    allowHashedSecrets,
    algorithm,
    expireKeys,
    notificationsEnabled,
    daysUntilExpiry,
    reportCadence,
    reportDay,
    sendReportToAdmin,
    startReporting,
    urgentReporting,
  } = valuesObj;
  const hashSettings = {
    plaintextAllowed: !!allowPlainSecrets,
    ...(allowHashedSecrets ? { algorithm } : {}),
  };
  let expirySettings = {
    enabled: expireKeys,
    expiryDays: daysUntilExpiry,
    reportAsUrgentBeforeDays: urgentReporting,
    startReportingBeforeDays: startReporting,
    notificationsEnabled,
  };
  if (notificationsEnabled) {
    expirySettings = {
      ...expirySettings,
      notifyAdminsIfNoOrgAdmins: !!sendReportToAdmin,
      reportCadence,
      reportDay,
    };
  }

  return {
    appSecretHashingMetadata: hashSettings,
    applicationApiKeyExpiry: expirySettings,
  };
};

const payloadToValues = (payload = {}) => {
  const {
    applicationApiKeyExpiry: {
      enabled,
      expiryDays,
      notificationsEnabled,
      reportCadence,
      reportDay,
      notifyAdminsIfNoOrgAdmins,
      reportAsUrgentBeforeDays,
      startReportingBeforeDays,
    } = { },
    appSecretHashingMetadata: {
      algorithm,
      plaintextAllowed,
    } = { },
  } = payload;

  return {
    allowPlainSecrets: !!plaintextAllowed,
    allowHashedSecrets: !!algorithm,
    ...(algorithm ? { algorithm } : {}),
    expireKeys: enabled,
    daysUntilExpiry: expiryDays,
    notificationsEnabled,
    ...(reportCadence ? { reportCadence } : {}),
    ...(reportDay ? { reportDay } : {}),
    sendReportToAdmin: !!notifyAdminsIfNoOrgAdmins,
    startReporting: startReportingBeforeDays,
    urgentReporting: reportAsUrgentBeforeDays,
  };
};

const preventSignsAndFraction = event => {
  if (event.key === '-' || event.key === '+' || event.key === '.') {
    event.preventDefault();
  }
};

export const ApplicationKeySettings = (props) => {
  const {
    apiKeySettings,
    classes,
    errorFields,
    errors,
    executeExpiryJob,
    executeExpiryReportingJob,
    expiryJobStatus,
    reportingJobStatus,
    fetchAPIKeySettings,
    isExecutingExpiryJob,
    isExecutingReportingJob,
    isLoading,
    updateAPIKeySettings,
    updateStatus,
    userContext = {},
  } = props;
  const intl = getI18n(useIntl());

  const defaultValues = {
    allowPlainSecrets: false,
    allowHashedSecrets: false,
    algorithm: 'SHA-512',
    expireKeys: false,
    daysUntilExpiry: 120,
    applyToKeysWithoutExpiry: false,
    applyToKeysWithLongerExpiry: false,
    notificationsEnabled: false,
    reportCadence: MONTHLY,
    reportDay: FIRST_DAY,
    sendReportToAdmin: false,
    startReporting: 90,
    urgentReporting: 60,
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [startReportingError, setStartReportingError] = useState(false);
  const [urgentReportingError, setUrgentReportingError] = useState(false);
  const [expiryReportingError, setExpiryReportingError] = useState(false);
  const {
    allowPlainSecrets,
    allowHashedSecrets,
    algorithm,
    expireKeys,
    daysUntilExpiry,
    applyToKeysWithoutExpiry,
    applyToKeysWithLongerExpiry,
    notificationsEnabled,
    reportCadence,
    reportDay,
    sendReportToAdmin,
    startReporting,
    urgentReporting,
  } = formValues;

  const initialValues = payloadToValues(apiKeySettings);
  const isFormDirty = areValuesDifferent(initialValues, formValues);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  // eslint-disable-next-line
  const daysUntilExpiryModified =  (daysUntilExpiry != initialValues.daysUntilExpiry) || (expireKeys !== initialValues.expireKeys);

  useEffect(() => {
    fetchAPIKeySettings();
  }, []);

  useEffect(() => {
    if (!userContext.portalAdmin) {
      props.push('/404');
    }
  }, [userContext]);

  useEffect(() => {
    if (expiryJobStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.expire.job.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    } else if (expiryJobStatus === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.settings.application.keysettings.expire.job.error'))
    }
  }, [expiryJobStatus]);

  useEffect(() => {
    if (reportingJobStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.reporting.job.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    } else if (reportingJobStatus === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.settings.application.keysettings.reporting.job.error'))
    }
  }, [reportingJobStatus]);

  useEffect(() => {
    if (updateStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.application.keysettings.update.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
      fetchAPIKeySettings();
    } else if (updateStatus === FAIL) {
      notifyMessages(ALERT_ERROR, errors || intl.getI18nMessage('label.settings.application.keysettings.update.error'));
      if (errorFields.length > 0) {
        const highLightError = errorFields[0];
        if (highLightError === 'startReportingBeforeDays') {
          setStartReportingError(true);
        } else if (highLightError === 'reportAsUrgentBeforeDays') {
          setUrgentReportingError(true);
        } else if (highLightError === 'expiryDays') {
          setExpiryReportingError(true);
        }
      }
    }
  }, [errorFields, updateStatus]);

  useEffect(() => {
    setFormValues({
      ...defaultValues,
      ...payloadToValues(apiKeySettings),
    });
  }, [apiKeySettings]);

 const handleCadenceChange = (value) => {
  let reportDayValue = MON_DAY;
  if (value === DAILY) {
    reportDayValue = '';
  } else if (value === MONTHLY) {
    reportDayValue = FIRST_DAY;
  } else {
    reportDayValue = MON_DAY;
  }
  setFormValues({
    ...formValues,
    reportCadence: value,
    reportDay: reportDayValue,
  })
 };

  const getSecretsLabel = ({ label, labelHelpText }) => (
    <div className={classes.secretLabelContainer}>
      <div className={classes.secretLabel}>
        {label}
      </div>
      <div className={classes.secretLabelHelpText}>
        {labelHelpText}
      </div>
    </div>
  );

  const boolFields = [
    'allowPlainSecrets',
    'allowHashedSecrets',
    'expireKeys',
    'applyToKeysWithoutExpiry',
    'applyToKeysWithLongerExpiry',
    'notificationsEnabled',
    'sendReportToAdmin',
  ];

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (boolFields.includes(name)) {
      if (name === 'expireKeys') {
        setStartReportingError(false);
        setUrgentReportingError(false);
        setExpiryReportingError(false);
        if (checked) {
          setFormValues({
            ...formValues,
            expireKeys: true,
            daysUntilExpiry: formValues.daysUntilExpiry || 120,
            startReporting: formValues.startReporting || 90,
            urgentReporting: formValues.urgentReporting || 60,
          });
        } else {
          setFormValues({
            ...formValues,
            expireKeys: false,
          });
        }
      } else {
        setFormValues({
          ...formValues,
          [name]: checked,
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  };

  const onCancel = () => {
    fetchAPIKeySettings();
  };
  const onSave = () => {
    setStartReportingError(false);
    setUrgentReportingError(false);
    setExpiryReportingError(false);
    updateAPIKeySettings(
      valuesToPayload(formValues),
      applyToKeysWithoutExpiry,
      applyToKeysWithLongerExpiry,
    );
  };

  const renderSharedSecretSecurity = () =>
    (<Fragment>
      <Typography variant="h6" className={classes.smallLabel}>
        {intl.getI18nMessage("label.settings.title")}
      </Typography>
      <Typography variant="h1" className={classes.pageTitle}>
        {intl.getI18nMessage("label.settings.application.keysettings")}
      </Typography>
      <Typography variant="h4">
        {intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.subtitle")}
      </Typography>
      <Typography variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.title.helpertext.1")}
      </Typography>
      <div className={classes.sharedSecretControls}>
        <Typography variant="body1">
          {intl.getI18nMessage("label.settings.application.keysettings.title.helpertext.2")}
        </Typography>
        <div className={classes.secretContainer}>
          <FormControlLabel
            control = {
              <Checkbox
                id="key-settings-allow-plain-secrets"
                name={'allowPlainSecrets'}
                onChange={handleInputChange}
                checked={allowPlainSecrets}
              />
            }
            label={
                getSecretsLabel({
                  label: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.plaintext.secrets"),
                  labelHelpText: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.plaintext.secrets.helptext"),
              })
            }
          />
        </div>
        <div className={classes.secretContainer}>
          <FormControlLabel
            control = {
              <Checkbox
                id="key-settings-allow-hashed-secrets"
                name={'allowHashedSecrets'}
                onChange={handleInputChange}
                checked={allowHashedSecrets}
              />
            }
            label={
              getSecretsLabel({
                label:  intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.hashed.secrets"),
                labelHelpText: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.hashed.secrets.helptext"),
              })
            }
          />
          <Collapse in={allowHashedSecrets}>
            <FormSelect
              id="key-settings-hash-algorithm-select"
              data={HASH_ALGORITHM_OPTIONS}
              handleChange={(value) => handleInputChange({ target: { name: 'algorithm', value: value } })}
              name={intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.hash.algorithm")}
              noNoneOption
              noNativeSelect
              value={algorithm}
            />
          </Collapse>
        </div>
      </div>
    </Fragment>);

  const renderStartCategory = () =>
    (<Fragment>
      <div>
        <b>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.field.label")}
        </b>
        &nbsp;
      </div>
      <div>{intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.1")}</div>
      <FormTextField
        fieldContainerClass={classes.categoryField}
        name={''}
        id={'key-settings-start-reporting'}
        data-apim-test={'key-settings-start-reporting'}
        variant="standard"
        value={startReporting}
        handleChange={(value) => handleInputChange({ target: { name: 'startReporting', value } })}
        onKeyPress={preventSignsAndFraction}
        error={startReportingError}
        helperText={''}
        textFieldClass={classes.categoryTextField}
        type={'number'}
      />
      <div>
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.2")}
        <span className={startReportingError ? classes.errorText : ''}>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.3")}
        </span>
      </div>
    </Fragment>);
  
  const renderUrgentCategory = () =>
    (<Fragment>
      <div>
        <b>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.field.label")}
        </b>
        &nbsp;
      </div>
      <div>{intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.1")}</div>
      <FormTextField
        fieldContainerClass={classes.categoryField}
        name={''}
        id={'key-settings-urgent-reporting'}
        data-apim-test={'key-settings-urgent-reporting'}
        variant="standard"
        value={urgentReporting}
        handleChange={(value) => handleInputChange({ target: { name: 'urgentReporting', value } })}
        onKeyPress={preventSignsAndFraction}
        error={urgentReportingError}
        helperText={''}
        textFieldClass={classes.categoryTextField}
        type={'number'}
      />
      <div>
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.2")}
        <span className={urgentReportingError ? classes.errorText : ''}>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.3")}
        </span>
      </div>
    </Fragment>);

  const renderStatusCategories = () =>
    (<Fragment>
      <div>
        <Typography variant="h4">
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.subtitle")}
        </Typography>
      </div>
      <Typography variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.subtitle.help.text")}
      </Typography>
      <List>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryStartItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.categoryListItemText,
            }}
          >
            {renderStartCategory()}
          </ListItemText>
        </ListItem>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryUrgentItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.categoryListItemText,
            }}
          >
            {renderUrgentCategory()}
          </ListItemText>
        </ListItem>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryNoExpiryItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText primary={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.no.expiry.help.text")} />
        </ListItem>
      </List>
    </Fragment>);
  
  const disableJobExecutionButton= isFormDirty;

  let reportDayOptions = MONTH_REPORT_DAY_OPTIONS;
  if ((reportCadence === WEEKLY) || (reportCadence === BIWEEKLY)) {
    reportDayOptions = WEEK_REPORT_DAY_OPTIONS;
  } else if (reportCadence === MONTHLY) {
    reportDayOptions = MONTH_REPORT_DAY_OPTIONS;
  } else {
    reportDayOptions = [];
  }
  
  const renderKeyExpiryReports = () =>
    (<Fragment>
      <div className={classes.keysSubtitleContainer}>
        <div>
          <Typography variant="h4">
            {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.subtitle")}
          </Typography>
        </div>
        <div>
          <Switch
            checked={notificationsEnabled}
            id={'key-settings-notifications-enabled'}
            onChange={(checked) => handleInputChange({ target: { name: 'notificationsEnabled', checked } })}
          />
        </div>
      </div>
      <Divider />
      <Typography className={classes.switchHelpText} variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.subtitle.helptext")}
      </Typography>
      <Collapse
        in={notificationsEnabled}
        unmountOnExit
      >
        <div className={classes.reportFieldContainer}>
          <FormSelect
            fieldContainerClass={classes.reportField}
            id="key-settings-report-cadence"
            data-apim-test="key-settings-report-cadence"
            data={KEY_EXPIRY_REPORT_CADENCE}
            handleChange={handleCadenceChange}
            name={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.report.cadence")}
            value={reportCadence}
            noNoneOption
            noNativeSelect
          />
          {(reportCadence !== DAILY) && <FormSelect
            fieldContainerClass={classes.reportField}
            id="key-settings-report-day"
            data-apim-test="key-settings-report-day"
            data={reportDayOptions}
            handleChange={(value) => handleInputChange({ target: { name: 'reportDay', value: value } })}
            name={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.report.day")}
            value={reportDay}
            noNoneOption
            noNativeSelect
          />}
        </div>
        <div>
          <FormControlLabel
            control = {
              <Checkbox
                data-apim-test="key-settings-sendReportToAdmin"
                name={'sendReportToAdmin'}
                onChange={handleInputChange}
                checked={sendReportToAdmin}
              />
            }
            label={intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.send.report.to.admin')}
          />
        </div>
        {renderReportingJobButton()}
      </Collapse>
    </Fragment>);
  
  const renderReportingJobButton = () => {
    return (<div className={classes.reportingJobButtonContainer}>
      <div>
        <Tooltip
          arrow
          placement="top"
          title={disableJobExecutionButton ? getI18nFormattedMessage('label.settings.application.keysettings.expire.job.button.tooltip') : ''}
        >
          <span>
            <Button
              className={classes.reportingJobBtn}
              data-apim-test="execute-reporting-job-button"
              disabled={disableJobExecutionButton}
              id={'execute-reporting-job-button'}
              onClick={executeExpiryReportingJob}
              variant="outlined"
              color="primary"
            >
              {intl.getI18nMessage("label.settings.application.keysettings.reporting.job.button.text")}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div
        className={classes.expireJobBtnHelpTextContainer}
      >
        <span
          className={classes.expireJobBtnHelpText}
        >
          {intl.getI18nMessage("label.settings.application.keysettings.reporting.job.button.helptext")}
        </span>
      </div>
    </div>);
  };

  const renderKeyExpiry = () =>
    (<Fragment>
        <div className={classes.keysSubtitleContainer}>
          <div>
            <Typography variant="h4">
              {intl.getI18nMessage("label.settings.application.keysettings.expire.keys.subtitle")}
            </Typography>
          </div>
          <div>
            <Switch
              checked={expireKeys}
              id={'key-settings-expire-keys'}
              onChange={(checked) => handleInputChange({ target: { name: 'expireKeys', checked } })}
            />
          </div>
        </div>
        <Divider />
        <Typography className={classes.switchHelpText} variant="body1">
          {intl.getI18nMessage("label.settings.application.keysettings.expire.keys.subtitle.helptext")}
        </Typography>
        <Collapse
          in={expireKeys}
          unmountOnExit
        >
          <FormTextField
            name={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.days.untill.expiry')}
            id={'key-settings-days-until-expiry'}
            data-apim-test={'key-settings-days-until-expiry'}
            variant="standard"
            value={`${daysUntilExpiry}`}
            handleChange={(value) => handleInputChange({ target: { name: 'daysUntilExpiry', value } })}
            onKeyPress={preventSignsAndFraction}
            error={expiryReportingError}
            helperText={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.days.untill.expiry.helptext')}
            type={'number'}
          />
          <Collapse
            in={daysUntilExpiryModified}
            unmountOnExit
          >
            <div>
              <FormControlLabel
                control = {
                  <Checkbox
                    data-apim-test={'key-settings-applyToKeysWithoutExpiry'}
                    name={'applyToKeysWithoutExpiry'}
                    onChange={handleInputChange}
                    checked={applyToKeysWithoutExpiry}
                  />
                }
                label={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.apply.without.expiry.date')}
              />
            </div>
            <div>
              <FormControlLabel
                control = {
                  <Checkbox
                    data-apim-test={'key-settings-applyToKeysWithLongerExpiry'}
                    name={'applyToKeysWithLongerExpiry'}
                    onChange={handleInputChange}
                    checked={applyToKeysWithLongerExpiry}
                  />
                }
                label={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.apply.longer.expiry.date')}
              />
            </div>
          </Collapse>
          {renderExpiryJobButton()}
          {renderStatusCategories()}
          {renderKeyExpiryReports()}
          <div className={classes.emptyBlock}/>
        </Collapse>
    </Fragment>);

  const renderExpiryJobButton = () => {
    return (<div className={classes.expiryJobButtonContainer}>
      <div>
        <Tooltip
          arrow
          placement="top"
          title={disableJobExecutionButton ? getI18nFormattedMessage('label.settings.application.keysettings.expire.job.button.tooltip') : ''}
        >
          <span>
            <Button
              className={classes.expireJobBtn}
              data-apim-test="execute-expiry-job-button"
              disabled={disableJobExecutionButton}
              id={'execute-expiry-job-button'}
              onClick={executeExpiryJob}
              variant="outlined"
              color="primary"
            >
              {intl.getI18nMessage("label.settings.application.keysettings.expire.job.button.text")}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div
        className={classes.expireJobBtnHelpTextContainer}
      >
        <span
          className={classes.expireJobBtnHelpText}
        >
          {intl.getI18nMessage("label.settings.application.keysettings.expire.job.button.helptext")}
        </span>
      </div>
    </div>);
  };

  return(
    <div className={classes.details}>
      {(isLoading || isExecutingExpiryJob || isExecutingReportingJob)
        && <Loading pageLoader={true} />}
      {renderSharedSecretSecurity()}
      {renderKeyExpiry()}
      {isFormDirty && <FormActionButtons
        id={"appBar"}
        cancelText={intl.getI18nMessage("label.cancel.button")}
        nextText={intl.getI18nMessage("label.save.button")}
        onCancelClick={onCancel}
        onNextClick={onSave}
        isDisabled={!isFormDirty}
      />}
      {notificationMessage !=='' && (
        <AlertMessages
        id={'application-settings-alert-messages'}
        variant={notificationStatus}
        message={notificationMessage}
        containerClass={classes.notificationWidthClass}
        onClose={() => notifyMessages('', '')}
        hasNotificationIcon={true}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  isError: getIsError(state),
  expiryJobStatus: getExpiryJobStatus(state),
  reportingJobStatus: getReportingJobStatus(state),
  errorFields: getErrorFields(state),
  errors: getErrors(state),
  isExecutingExpiryJob: getIsExecutingExpiryJob(state),
  isExecutingReportingJob: getIsExecutingReportingJob(state),
  isLoading: getIsLoading(state),
  apiKeySettings: getAPIKeySettings(state),
  updateStatus: getUpdateKeySettingsStatus(state),
  userContext: getUserDetails(state),
});

const mapDispatchToProps = {
  executeExpiryJob,
  executeExpiryReportingJob,
  fetchAPIKeySettings,
  updateAPIKeySettings,
  push,
};

ApplicationKeySettings.propTypes = {
  classes: PropTypes.object,
  isError: PropTypes.bool,
  errorFields: PropTypes.array,
  errors: PropTypes.array,
  expiryJobStatus: PropTypes.string,
  isExecutingExpiryJob: PropTypes.bool,
  reportingJobStatus: PropTypes.string,
  isExecutingReportingJob: PropTypes.bool,
  isLoading: PropTypes.bool,
  apiKeySettings: PropTypes.object,
  history: PropTypes.object,
  updateStatus: PropTypes.string,
  executeExpiryJob: PropTypes.func,
  executeExpiryReportingJob: PropTypes.func,
  fetchAPIKeySettings: PropTypes.func,
  updateAPIKeySettings: PropTypes.func,
  userContext: PropTypes.object,
  push: PropTypes.func,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps))(ApplicationKeySettings);
